import React from 'react';
import styled from 'styled-components';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Alert } from '@brokerbay/design-components';
import { FileViewer, Text, Button, Link, Popconfirm, theme } from 'components';
import { AuthContext } from 'contexts';
import DocumentsContext from './DocumentsContext';
import RejectionModal from './RejectionModal';
import UploadModal from './UploadModal';

const DocumentViewer: React.FC = () => {
  const { documentId } = useParams<Record<string, string>>();
  const {
    documentsToView,
    listing,
    canApproveReject,
    override,
    listingAgreement,
  } = React.useContext(DocumentsContext);
  const { auth } = React.useContext(AuthContext);
  const [rejectionOpen, setRejectionOpen] = React.useState(false);
  const [replaceModalOpen, setReplaceModalOpen] = React.useState(false);

  const zeroIndex = (pageNumber = 1) => pageNumber - 1;

  const renderOverride = (color: 'success' | 'danger') => (
      <Popconfirm
        title="Are you sure you want to override this document back to Pending Review?"
        width={240}
        onConfirm={override}
        okText="Override"
      >
        <Link color={color}>Override</Link>
      </Popconfirm>
    );

  const renderAlert = () => {
    // There can be no listing agreement if listing is created by admin
    if (
      !listingAgreement ||
      !['all', listingAgreement.id].includes(documentId)
    ) {
      return null;
    }

    let type: 'warning' | 'error' | 'success';
    let message: string | JSX.Element;

    const { reason } = listingAgreement;

    switch (listing.documentStatus) {
      case 'uploaded':
        type = 'warning';
        message = (
          <>
            <Text block>
              This draft listing has not been submitted for administrator
              approval yet.
            </Text>
            <Link>
              {auth && !auth.isAdmin && (
                <RouterLink to={`/listing/${listing.id}/edit?submit=true`}>
                  Submit for Approval
                </RouterLink>
              )}
            </Link>
          </>
        );
        break;
      case 'pending':
        type = 'warning';
        message = 'This document is pending review';
        break;
      case 'rejected':
        type = 'error';
        message = (
          <>
            <Text block>This document has been rejected</Text>
            <Text block type="secondary">
              &quot;{reason}&quot;
            </Text>
            {canApproveReject ? (
              <div className="alert-button-container">
                <div>
                  <Link color="danger" onClick={() => setRejectionOpen(true)}>
                    Edit Reason
                  </Link>
                  |{renderOverride('danger')}
                </div>
              </div>
            ) : (
              <Link color="danger" onClick={() => setReplaceModalOpen(true)}>
                Replace
              </Link>
            )}
          </>
        );
        break;
      case 'accepted':
        type = 'success';
        message = (
          <>
            <Text>
              {canApproveReject
                ? 'This document has been accepted and an email has been sent to the listing agent.'
                : 'This document has been accepted.'}
            </Text>{' '}
            {canApproveReject && renderOverride('success')}
          </>
        );
        break;
      default:
        break;
    }
    return <StyledAlert showIcon type={type} message={message} />;
  };

  return (
    <div>
      {renderAlert()}
      <RejectionModal
        visible={rejectionOpen}
        setVisible={setRejectionOpen}
        editingReason
      />
      {replaceModalOpen && (
        <UploadModal
          title="Replace Document"
          visible={replaceModalOpen}
          onCancel={() => setReplaceModalOpen(false)}
          replacingId={listingAgreement.id}
          documentType="listingAgreement"
        />
      )}
      <FileViewer
        files={documentsToView}
        render={(Image, file, page) => (
          <StyledDoc id={`${file.id}-${zeroIndex(page?.pageNumber)}`}>
            {Image}
          </StyledDoc>
        )}
      />
    </div>
  );
};

const StyledAlert = styled(Alert)`
  margin-bottom: 25px;
  .alert-button-container {
    display: inline-block;
    > div {
      display: grid;
      grid-template-columns: auto auto auto;
      grid-column-gap: 8px;
    }
  }
  ${Button} {
    padding: 0;
  }
`;

const StyledDoc = styled.div`
  box-shadow: ${theme.getElevation()};
`;

export default DocumentViewer;
